import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminConsole = ({ user }: any) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/posts`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch posts', err);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/feedback`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setFeedback(res.data);
    } catch (err) {
      console.error('Failed to fetch feedback', err);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'posts') fetchPosts();
    else if (activeTab === 'feedback') fetchFeedback();
  }, [activeTab, fetchUsers, fetchPosts, fetchFeedback]);

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/users/${editingUser.id}`, editingUser, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user', err);
      alert('Update failed');
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (window.confirm('Are you sure? This action is permanent.')) {
      try {
        await axios.delete(`${API_URL}/admin/users/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchUsers();
      } catch (err) {
        console.error('Failed to delete user', err);
      }
    }
  };

  const handleDeletePost = async (type: string, id: number) => {
    if (window.confirm('Delete this post?')) {
      try {
        await axios.delete(`${API_URL}/admin/posts/${type}/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchPosts();
      } catch (err) {
        console.error('Failed to delete post', err);
      }
    }
  };

  const handleToggleLock = async (post: any) => {
    try {
        await axios.put(`${API_URL}/admin/posts/${post.type}/${post.id}`, {
            ...post,
            is_locked: post.is_locked ? 0 : 1
        }, {
            headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchPosts();
    } catch (err) {
        console.error('Failed to lock/unlock post', err);
    }
  };

  const handleUpdateFeedbackStatus = async (id: number, status: string) => {
    try {
      await axios.put(`${API_URL}/admin/feedback/${id}`, { status }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchFeedback();
    } catch (err) {
      console.error('Failed to update feedback status', err);
    }
  };

  return (
    <div className="content">
      <h2 style={{ color: 'var(--gold)', textAlign: 'center', fontSize: '2.5rem', marginBottom: '2rem' }}>Admin Sanctum</h2>
      
      <div className="tabs" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '2rem' }}>
        <button onClick={() => setActiveTab('users')} className={activeTab === 'users' ? 'active' : ''}>User Management</button>
        <button onClick={() => setActiveTab('posts')} className={activeTab === 'posts' ? 'active' : ''}>Post Management</button>
        <button onClick={() => setActiveTab('feedback')} className={activeTab === 'feedback' ? 'active' : ''}>Feedback Vault</button>
      </div>

      {loading && <div className="spinner"></div>}

      {!loading && activeTab === 'users' && (
        <div className="card">
          <h3>Users ({users.length})</h3>
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gold)', textAlign: 'left' }}>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u: any) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #333' }}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>{u.display_name || '---'}</td>
                    <td>{u.is_admin ? 'Admin' : 'Seeker'}</td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0' }}>
                      <button onClick={() => setEditingUser(u)} style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}>Rename/Reset</button>
                      {u.id !== user.id && (
                        <button onClick={() => handleDeleteUser(u.id)} style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#8b0000' }}>Exile</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === 'posts' && (
        <div className="card">
          <h3>Collective Wisdom ({posts.length} entries)</h3>
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gold)', textAlign: 'left' }}>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Title/Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p: any) => (
                  <tr key={`${p.type}-${p.id}`} style={{ borderBottom: '1px solid #333' }}>
                    <td>{p.id}</td>
                    <td><span className="badge" style={{ fontSize: '0.7rem' }}>{p.type.toUpperCase()}</span></td>
                    <td>{p.title}</td>
                    <td>{p.is_locked ? '🔒 Locked' : '🔓 Active'}</td>
                    <td style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0' }}>
                      <button onClick={() => handleToggleLock(p)} style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', backgroundColor: p.is_locked ? 'var(--gold)' : '#444', color: p.is_locked ? 'black' : 'white' }}>
                        {p.is_locked ? 'Unlock' : 'Lock'}
                      </button>
                      <button onClick={() => handleDeletePost(p.type, p.id)} style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#8b0000' }}>Vanish</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === 'feedback' && (
        <div className="feedback-list">
          <h3>Received Feedback</h3>
          {feedback.length === 0 && <p>The oracle is silent.</p>}
          {feedback.map((f: any) => (
            <div key={f.id} className="card" style={{ borderLeft: `4px solid ${f.status === 'open' ? 'var(--gold)' : 'var(--accent-purple)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <small>From: <strong>{f.username || 'Anonymous Seeker'}</strong> | {new Date(f.created_at).toLocaleString()}</small>
                <span className="badge" style={{ backgroundColor: f.status === 'open' ? 'var(--gold)' : '#222', color: f.status === 'open' ? 'black' : 'white' }}>{f.status.toUpperCase()}</span>
              </div>
              <p style={{ margin: '1rem 0', fontStyle: 'italic' }}>"{f.content}"</p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  onClick={() => handleUpdateFeedbackStatus(f.id, f.status === 'open' ? 'resolved' : 'open')}
                  style={{ padding: '0.3rem 1rem', fontSize: '0.9rem' }}
                >
                  Mark as {f.status === 'open' ? 'Resolved' : 'Open'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editing Modals/Overlays */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="card modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <h3>Manage Seeker: {editingUser.username}</h3>
            <form onSubmit={handleUpdateUser}>
              <label>Username</label>
              <input type="text" value={editingUser.username} onChange={e => setEditingUser({ ...editingUser, username: e.target.value })} required />
              <label>Display Name</label>
              <input type="text" value={editingUser.display_name || ''} onChange={e => setEditingUser({ ...editingUser, display_name: e.target.value })} />
              <label>New Password (leave blank to keep current)</label>
              <input type="password" value={editingUser.password || ''} onChange={e => setEditingUser({ ...editingUser, password: e.target.value })} />
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="submit">Update Seeker</button>
                <button type="button" onClick={() => setEditingUser(null)} style={{ backgroundColor: '#444' }}>Close</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConsole;
